import React, { useRef } from 'react';
import { ModalInformation, TicketActions, TicketButton } from './TicketButton';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ISonURL } from '../../../types/IUrl';
import { StatusInfoIcon } from '../../../_lib/components/StatusInfo/partials/StatusInfoIcon';
import { Icons } from '../../../components/atoms/SVG';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { PersonInformation, PersonInformationModifier } from '../../../components/molecules/PersonInformation';
import { StatusInfoIconModifier } from '../../../_lib/components/StatusInfo/partials/StatusInfoIcon';

export type Ticket = {
  ticketId: string;
  startDateTime: string;
  endDateTime: string;
  consumer?: ISonURL;
  ticketState?: 'open' | 'bookedByCurrentUser' | 'bookedByOtherUser';
  actions?: TicketActions;
};

type TicketProps = { ticket: Ticket; openModal: (props: ModalInformation) => void };

export function Ticket({ ticket, openModal }: TicketProps) {
  const { i18n, t } = useSonTranslation('TicketBooking');
  const ticketRef = useRef<HTMLDivElement>(null);
  const startDayTime = new Date(ticket.startDateTime);
  const start = startDayTime.toLocaleTimeString(i18n.language, { timeStyle: 'short' });
  const endDayTime = new Date(ticket.endDateTime);
  const end = endDayTime.toLocaleTimeString(i18n.language, { timeStyle: 'short' });
  const timeFrame = t('default.fromTo', {
    start,
    end,
  });

  let className = 'ticket-booking__ticket';

  if (ticket.ticketState === 'bookedByOtherUser' && !ticket.consumer) {
    className += ' ticket-booking__ticket--booked';
  }

  const consumerInfo = (
    <>
      <Strong>{t('default.bookedByOtherUserWithConsumer')}</Strong>{' '}
      <div className="ticket-booking__state-consumer">
        <PersonInformation modifier={PersonInformationModifier.TICKET} profileLink={ticket.consumer} />
      </div>
    </>
  );

  return (
    <div ref={ticketRef} id={ticket.ticketId} className={className} tabIndex={-1}>
      {ticket.ticketState === 'bookedByCurrentUser' ||
      (ticket.ticketState === 'bookedByOtherUser' && ticket.consumer) ? (
        <div className="ticket-booking__status-info">
          <StatusInfoIcon icon={Icons.FEEDBACK_POSITIVE_CHECK} modifier={StatusInfoIconModifier.GREEN} />
        </div>
      ) : null}
      <div className="font-regular-13-19">
        <Strong>
          <time dateTime={ticket.startDateTime}>{start}</time>
          {'\u00A0'}
        </Strong>
        {t('DateRanger:until')}
        {'\u00A0'}
        <Strong>
          <time dateTime={ticket.endDateTime}>{end}</time>
          {'\u00A0'}
          {t('DateRanger:oClock')}
        </Strong>
        {(ticket.ticketState === 'bookedByCurrentUser' || ticket.ticketState === 'bookedByOtherUser') && (
          <>
            {/* Changed <p> to <div> as <p> is causing an error when PersonInformation component is renderd as child.
            PersonInformation itself contains <div> and <p> tags that are not allowwed as children of <p> */}
            <div className="ticket-booking__state">
              {ticket.ticketState === 'bookedByCurrentUser' && !ticket.consumer ? (
                <Strong>{t('default.booked')}</Strong>
              ) : null}
              {ticket.ticketState === 'bookedByCurrentUser' && ticket.consumer ? consumerInfo : null}
              {ticket.ticketState === 'bookedByOtherUser' && !ticket.consumer ? (
                <Strong>{t('default.bookedByOtherUser')}</Strong>
              ) : null}
              {ticket.ticketState === 'bookedByOtherUser' && ticket.consumer ? consumerInfo : null}
            </div>
          </>
        )}
      </div>
      <TicketButton
        timeFrame={timeFrame}
        openModal={openModal}
        ticketState={ticket.ticketState ? ticket.ticketState : ``}
        ticketRef={ticketRef}
        consumer={ticket.consumer}
        {...ticket.actions}
      />
    </div>
  );
}
